import { Button, DataGrid, DropDownButton, DateBox, Popup, FileUploader } from "devextreme-react";
import { Column, ColumnChooser, DataGridRef, DataGridTypes, Editing, Export, FilterBuilderPopup, FilterPanel, FilterRow, HeaderFilter, Item, LoadPanel, RowDragging, Scrolling, SearchPanel, Selection, Sorting, Toolbar } from "devextreme-react/cjs/data-grid";
import { DropDownButtonTypes } from "devextreme-react/drop-down-button";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { jsPDF as JsPdf } from 'jspdf';
import { saveAs } from 'file-saver-es';
import { Workbook } from 'exceljs';
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter';
import { exportDataGrid as exportDataGridToXLSX } from 'devextreme/excel_exporter';
import notify from "devextreme/ui/notify";
import { appInfo } from "../../app-info";
import { base64Preview, getShipmentDetailRequsts, getShipmentLinesRequsts, createShipment } from "../../api/requests";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport, faEye, faFilePdf, faEnvelope, faFileImport, faSpinner, faFileEdit } from "@fortawesome/free-solid-svg-icons";

interface ShipmentItem {
  id: string;
  itemCode: string;
  itemName: string;
  itemBarcode: number;
  lineQuantity: number;
  itemBaseQuantity: number;
  currentQuantity: number;
  isCompleted?: boolean;
}

interface Order {
  pickListRef: number;
  pickListStatus: number;
  pickListKey: string;
  orderDate: string;
  orderNr: string;
  orderTotal: number;
  erpOrderRef: number;
  arpCode: string;
  arpName: string;
  arpLanguage: string;
  arpShipInfoAdr: string;
  erpDeliveryPartyRef: number;
  erpSendingPartyRef: number;
  shipAgentRef: number;
  shipTypeRef: number;
  docTrackingNr: string;
  sourceWarehouse: number;
  deliveryConfirmation: number;
  orderConfirmation: number;
  comment: string;
}

interface PackageItem {
  id: string;
  itemCode: string;
  itemName: string;
  quantity: number;
  packageNumber: number;
  trackingNr: string;
}

export const ShipmentTrackingPage: React.FC = () => {
  const { t } = useTranslation();
  const gridRef = useRef(null);

  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [items, setItems] = useState<ShipmentItem[]>([]);
  const [popupVisible, setPopupVisible] = useState(false);
  const [showFilterRow, setShowFilterRow] = useState(false);
  const [packages, setPackages] = useState<PackageItem[]>([]);
  const [currentPackage, setCurrentPackage] = useState(1);
  const [packageCapacity] = useState(3); // Her paket 3 ürün alabiliyor

  const today = new Date();
  const [startDate, setStartDate] = useState(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30));
  const [endDate, setEndDate] = useState(today);

  const isAllCompleted = items.length > 0 && items.every(item => item.currentQuantity === item.lineQuantity);

  useEffect(() => {
    fetchOrders();
  }, [startDate, endDate]);

  const fetchOrders = async () => {
    try {
      const response = await getShipmentLinesRequsts(startDate, endDate,1);
      if (response.resCode === 200) {
        setOrders(response.dataset || []);
      } else {
        notify(response.resMessage, 'error');
      }
    } catch {
      notify('Siparişler alınamadı', 'error');
    }
  };

  const handleOrderClick = (order: Order) => {
    setSelectedOrder(order);
    setPopupVisible(true);
    setPackages([]);
    setCurrentPackage(1);
    loadOrderDetails(order.erpOrderRef.toString());
  };

  const loadOrderDetails = async (orderId: string) => {
    try {
      const response = await getShipmentDetailRequsts(orderId);
      if (response.resCode === 200) {
        const shipmentItems: ShipmentItem[] = response.pickListLines.map((item: any) => ({
          id: item.lineNo_.toString(),
          itemCode: item.itemCode,
          itemName: item.itemDescription,
          itemBarcode: item.itemBarcode,
          lineQuantity: item.amount,
          itemBaseQuantity: item.packageContent,
          currentQuantity: 0,
          isCompleted: false
        }));
        setItems(shipmentItems);
      } else {
        notify(response.resMessage, 'error');
      }
    } catch (error) {
      notify('Sipariş detayları alınamadı', 'error');
    }
  };

  const handleQuantityChange = (itemId: string, increment: boolean): void => {
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const newQuantity = increment ? item.currentQuantity + 1 : item.currentQuantity - 1;
        const finalQuantity = Math.max(0, Math.min(newQuantity, item.lineQuantity));
        const isCompleted = finalQuantity === item.lineQuantity;

        // Eğer miktar artırıldıysa, pakete ekle
        if (increment) {
          addToPackage({
            ...item,
            currentQuantity: 1, // Her seferinde 1 miktar ekle
            isCompleted: isCompleted
          });
        }
        
        return {
          ...item,
          currentQuantity: finalQuantity,
          isCompleted: isCompleted
        };
      }
      return item;
    }));
  };

  const addToPackage = (item: ShipmentItem) => {
    if (item.currentQuantity === 0) return;
    
    const currentPackageItems = packages.filter(p => p.packageNumber === currentPackage);
    
    if (currentPackageItems.length >= packageCapacity) {
      notify('Bu paket dolu! Yeni paket oluşturuluyor.', 'info');
      setCurrentPackage(prev => prev + 1);
    }
    
    const trackingNr = `${selectedOrder?.docTrackingNr}-PKT${currentPackage}`;
    
    const newPackageItem: PackageItem = {
      id: `${item.id}-${Date.now()}`,
      itemCode: item.itemCode,
      itemName: item.itemName,
      quantity: 1,
      packageNumber: currentPackageItems.length >= packageCapacity ? currentPackage + 1 : currentPackage,
      trackingNr: trackingNr
    };
    
    setPackages(prev => [...prev, newPackageItem]);
    
    setItems(prev => prev.map(i => 
      i.id === item.id ? { ...i, currentQuantity: i.currentQuantity - 1 } : i
    ));
  };

  const removeFromPackage = (packageItemId: string) => {
    setPackages(prev => prev.filter(p => p.id !== packageItemId));
  };

  const getPackageNumbers = () => {
    const packageNumbers = [...new Set(packages.map(p => p.packageNumber))];
    return packageNumbers.sort((a, b) => a - b);
  };

  const onExporting = (e: any) => {
    if (e.format === 'pdf') {
      const doc = new JsPdf();
      exportDataGridToPdf({
        jsPDFDocument: doc,
        component: e.component,
      }).then(() => {
        doc.save('ShipmentTracking.pdf');
      });
    } else {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet('Page1');

      exportDataGridToXLSX({
        component: e.component,
        worksheet,
        autoFilterEnabled: true,
      }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'ShipmentTracking.xlsx');
        });
      });
      e.cancel = true;
    }
  };

  return (
    <div className="view crm-contact-list">
      <div className="view-wrapper view-wrapper-contact-list" style={{ width: '100%', height: '100%' }}>
        <div style={{ width: '100%', height: '100%', padding: '20px' }}>
          <DataGrid
            className='custom-grid compact-grid'
            focusedRowEnabled={false}
            dataSource={orders}
            keyExpr="erpOrderRef"
            noDataText={t("noData")}
            allowColumnReordering
            allowColumnResizing
            columnResizingMode={"widget"}
            showBorders
            ref={gridRef}
            repaintChangesOnly={true}
            remoteOperations={false}
            onRowClick={(e) => handleOrderClick(e.data)}
            style={{ width: '100%', height: '100%' }}
            onExporting={onExporting}
          >
            <SearchPanel visible placeholder={t("Ara")} />
            <ColumnChooser title={t("columnChooser")} enabled />
            <FilterRow visible={showFilterRow} applyFilter={"auto"} />
            
            <HeaderFilter visible />
            <Sorting mode='multiple' />
            <Scrolling mode='virtual' />
            <Selection
              mode="multiple"
              selectAllMode="page"
              showCheckBoxesMode="always"
              deferred={false}
            />
            <FilterPanel visible />
            <FilterBuilderPopup width={"50%"} height={"50%"} title={t("filterBuilder")} />

            <Toolbar>
              <Item name='searchPanel' location='before' />

              <Item location='before' locateInMenu='auto'>
                <DateBox
                  defaultValue={startDate}
                  onValueChanged={(e) => setStartDate(e.value)}
                  displayFormat="dd.MM.yyyy"
                  calendarOptions={{
                    firstDayOfWeek: 1,
                  }}
                />
              </Item>

              <Item location='before' locateInMenu='auto'>
                <DateBox
                  defaultValue={endDate}
                  onValueChanged={(e) => setEndDate(e.value)}
                  displayFormat="dd.MM.yyyy"
                  calendarOptions={{
                    firstDayOfWeek: 1,
                  }}
                />
              </Item>

              <Item location='before' locateInMenu='auto'>
                <Button
                  icon='refresh'
                  hint={t("refresh")}
                  stylingMode='text'
                  onClick={fetchOrders}
                />
              </Item>

              <Item name='columnChooserButton' location='before' />

              /*<Item location='before'>
                <Button
                  icon='endswith'
                  hint={t("detailedSearch")}
                  stylingMode={showFilterRow ? "contained" : "text"}
                  onClick={() => setShowFilterRow(!showFilterRow)}
                />
              </Item>*/

              <Item name='exportButton' location='before' />

              <Item location='center'>
                {orders.length} {t("records")}
              </Item>
            </Toolbar>

            <Column 
              dataField="pickListStatus" 
              caption={t("status")} 
              dataType="string"               
            />
            <Column 
              dataField="orderDate" 
              caption={t("date")}
              dataType="date"
              format="dd.MM.yyyy"  
            />
            <Column 
              dataField="orderNr" 
              caption={t("OrderNo")} 
            />
            <Column 
              dataField="arpName" 
              caption={t("Müşteri Adı")} 
            />
            <Column 
              dataField="arpShipInfoAdr" 
              caption={t("Müşteri Adresi")} 
            />
            <Column 
              dataField="docTrackingNr" 
              caption={t("docTrackingNo")}  
            />
            <Column 
              dataField="deliveryConfirmation" 
              caption={t("deliveryConfirmation")}
              alignment="left"
              calculateCellValue={(rowData) => {
                switch (rowData.deliveryConfirmation) {
                  case 0:
                    return t("waiting");
                  case 1:
                    return t("approved");
                  case 2:
                    return t("rejected");
                  default:
                    return "";
                }
              }}
            />
            <Column 
              dataField="sourceWarehouse" 
              caption={t("sourceWarehouse")} 
            />
            <Column 
              dataField="orderTotal" 
              caption={t("Tutar")} 
            />
          </DataGrid>
        </div>

        <Popup
          visible={popupVisible}
          onHiding={() => setPopupVisible(false)}
          title={`Sipariş Detayı: ${selectedOrder?.orderNr}`}
          width="98%"
          height="100%"
          showCloseButton
        >
          <div className="p-4" style={{ height: '100%', width: '100%', display: 'flex', gap: '15px' }}>
            {/* Auftragsdetails Panel - Sol */}
            <div style={{ 
              width: '20%', 
              backgroundColor: '#f8f9fa', 
              border: '1px solid #dee2e6', 
              borderRadius: '8px',
              padding: '15px',
              height: '92%',
              fontSize: '12px',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <h3 style={{ 
                margin: '0 0 15px 0', 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#495057',
                borderBottom: '1px solid #dee2e6',
                paddingBottom: '8px'
              }}>
                Auftragsdetails
              </h3>
              
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '8px',
                flex: 1,
                overflowY: 'auto'
              }}>
                <div>
                  <label style={{ 
                    fontSize: '10px', 
                    fontWeight: '600', 
                    color: '#6c757d', 
                    display: 'block',
                    marginBottom: '2px'
                  }}>
                    Bestellnummer
                  </label>
                  <span style={{ fontSize: '11px', color: '#212529' }}>
                    {selectedOrder?.orderNr || '-'}
                  </span>
                </div>

                <div>
                  <label style={{ 
                    fontSize: '10px', 
                    fontWeight: '600', 
                    color: '#6c757d', 
                    display: 'block',
                    marginBottom: '2px'
                  }}>
                    Erstellt am
                  </label>
                  <span style={{ fontSize: '11px', color: '#212529' }}>
                    {selectedOrder?.orderDate ? new Date(selectedOrder.orderDate).toLocaleDateString('de-DE') : '-'}
                  </span>
                </div>

                <div>
                  <label style={{ 
                    fontSize: '10px', 
                    fontWeight: '600', 
                    color: '#6c757d', 
                    display: 'block',
                    marginBottom: '2px'
                  }}>
                    Versandart
                  </label>
                  <span style={{ fontSize: '11px', color: '#212529' }}>
                    {selectedOrder?.shipTypeRef === 0 ? 'Selbstabholer' : 'Standard'}
                  </span>
                </div>

                <div>
                  <label style={{ 
                    fontSize: '10px', 
                    fontWeight: '600', 
                    color: '#6c757d', 
                    display: 'block',
                    marginBottom: '2px'
                  }}>
                    Lieferadresse (Straße)
                  </label>
                  <span style={{ fontSize: '11px', color: '#212529' }}>
                    {selectedOrder?.arpShipInfoAdr || '-'}
                  </span>
                </div>

                <div>
                  <label style={{ 
                    fontSize: '10px', 
                    fontWeight: '600', 
                    color: '#6c757d', 
                    display: 'block',
                    marginBottom: '2px'
                  }}>
                    Lieferadresse (Ort)
                  </label>
                  <span style={{ fontSize: '11px', color: '#212529' }}>
                    -
                  </span>
                </div>

                <div>
                  <label style={{ 
                    fontSize: '10px', 
                    fontWeight: '600', 
                    color: '#6c757d', 
                    display: 'block',
                    marginBottom: '2px'
                  }}>
                    Lieferadresse (Land)
                  </label>
                  <span style={{ fontSize: '11px', color: '#212529' }}>
                    -
                  </span>
                </div>

                <div>
                  <label style={{ 
                    fontSize: '10px', 
                    fontWeight: '600', 
                    color: '#6c757d', 
                    display: 'block',
                    marginBottom: '2px'
                  }}>
                    Logistiker
                  </label>
                  <span style={{ fontSize: '11px', color: '#212529' }}>
                    -
                  </span>
                </div>

                <div>
                  <label style={{ 
                    fontSize: '10px', 
                    fontWeight: '600', 
                    color: '#6c757d', 
                    display: 'block',
                    marginBottom: '2px'
                  }}>
                    Bestellhinweis
                  </label>
                  <span style={{ fontSize: '11px', color: '#212529' }}>
                    {selectedOrder?.comment || '-'}
                  </span>
                </div>

                <div>
                  <label style={{ 
                    fontSize: '10px', 
                    fontWeight: '600', 
                    color: '#6c757d', 
                    display: 'block',
                    marginBottom: '2px'
                  }}>
                    Kunde
                  </label>
                  <span style={{ fontSize: '11px', color: '#212529' }}>
                    {selectedOrder?.arpName || '-'}
                  </span>
                </div>

                <div>
                  <label style={{ 
                    fontSize: '10px', 
                    fontWeight: '600', 
                    color: '#6c757d', 
                    display: 'block',
                    marginBottom: '2px'
                  }}>
                    Firma (Empfänger)
                  </label>
                  <span style={{ fontSize: '11px', color: '#212529' }}>
                    -
                  </span>
                </div>
              </div>
            </div>

            {/* DataGrid - Orta */}
            <div style={{ flex: '1', display: 'flex', flexDirection: 'column', height: '100%' }}>
              <DataGrid 
                dataSource={items} 
                keyExpr="id" 
                showBorders
                style={{ height: '70%', width: '100%' }}
                rowAlternationEnabled={true}
                repaintChangesOnly={false}
                onCellPrepared={(e) => {
                  if (e.rowType === 'data') {
                    const isCompleted = e.data.currentQuantity === e.data.lineQuantity;
                    if (isCompleted) {
                      e.cellElement.style.backgroundColor = '#d4edda';
                      e.cellElement.style.color = '#155724';
                      e.cellElement.style.fontWeight = '500';
                    } else {
                      e.cellElement.style.backgroundColor = '';
                      e.cellElement.style.color = '';
                      e.cellElement.style.fontWeight = '';
                    }
                  }
                }}
              >
                <Column
                  caption="Fotoğraf"
                  width={60}
                  alignment="center"
                  cellRender={() => (
                    <div style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto'
                    }}>
                      
                    </div>
                  )}
                />
                <Column 
                  dataField="itemCode" 
                  caption={t("Ürün Kodu")} 
                  alignment="left"
                />
                <Column 
                  dataField="itemName" 
                  caption={t("itemName")} 
                  alignment="left"
                />
                <Column 
                  dataField="itemBarcode" 
                  caption={t("itemBarcode")} 
                  alignment="center"
                />
                <Column 
                  dataField="lineQuantity" 
                  caption={t("Hedef Miktar")} 
                  width={120}
                  alignment="center"
                  dataType="number"
                />
                <Column
                  dataField="currentQuantity"
                  caption={t("Mevcut Miktar")}
                  width={180}
                  alignment="center"
                  cellRender={({ data }) => (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      gap: '8px',
                      padding: '8px',
                      margin: '-8px',
                      borderRadius: '4px'
                    }}>
                      <span style={{ 
                        minWidth: '40px',
                        textAlign: 'center',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#495057'
                      }}>
                        {data.currentQuantity}
                      </span>
                      {data.currentQuantity < data.lineQuantity && (
                        <Button
                          icon="plus"
                          stylingMode="text"
                          hint="Artır"
                          onClick={() => handleQuantityChange(data.id, true)}
                          style={{
                            minWidth: '30px',
                            height: '30px',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            fontSize: '14px'
                          }}
                        />
                      )}
                    </div>
                  )}
                />
              </DataGrid>

              {/* Paketleme Alanı */}
              <div style={{ 
                marginTop: '2%', 
                padding: '2%', 
                backgroundColor: '#f8f9fa', 
                border: '1px solid #dee2e6', 
                borderRadius: '8px',
                height: '20%'
              }}>
                <h3 style={{ 
                  margin: '0 0 2% 0', 
                  fontSize: '16px', 
                  fontWeight: '600', 
                  color: '#495057'
                }}>
                  Paketleme Bilgileri
                </h3>
                <div style={{ display: 'flex', gap: '2%' }}>
                  <div>
                    <label style={{ 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      color: '#6c757d', 
                      display: 'block',
                      marginBottom: '1%'
                    }}>
                      Hacim (m³)
                    </label>
                    <span style={{ fontSize: '14px', color: '#212529' }}>
                      -
                    </span>
                  </div>
                  <div>
                    <label style={{ 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      color: '#6c757d', 
                      display: 'block',
                      marginBottom: '1%'
                    }}>
                      Ağırlık (kg)
                    </label>
                    <span style={{ fontSize: '14px', color: '#212529' }}>
                      -
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Paketler Paneli - Sağ */}
            <div style={{ 
              width: '25%', 
              backgroundColor: '#f8f9fa', 
              border: '1px solid #dee2e6', 
              borderRadius: '8px',
              padding: '15px',
              height: 'fit-content',
              maxHeight: '90%',
              overflowY: 'auto'
            }}>
              <h3 style={{ 
                margin: '0 0 15px 0', 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#495057',
                borderBottom: '2px solid #dee2e6',
                paddingBottom: '10px'
              }}>
                Paketler ({getPackageNumbers().length})
              </h3>

              <div style={{
                marginBottom: '15px',
                padding: '12px',
                backgroundColor: 'white',
                border: '1px solid #dee2e6',
                borderRadius: '6px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '10px'
                }}>
                  <h4 style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    Tracking Nr.
                  </h4>
                  <span style={{
                    fontSize: '12px',
                    color: '#6c757d'
                  }}>
                    {selectedOrder?.docTrackingNr || '-'}
                  </span>
                </div>
              </div>

              {getPackageNumbers().length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  color: '#6c757d', 
                  fontSize: '14px',
                  padding: '20px 0'
                }}>
                  Henüz paket oluşturulmadı
                </div>
              ) : (
                getPackageNumbers().map(packageNum => {
                  const packageItems = packages.filter(p => p.packageNumber === packageNum);
                  const packageTrackingNr = packageItems[0]?.trackingNr || '-';
                  
                  return (
                    <div key={packageNum} style={{
                      marginBottom: '15px',
                      padding: '12px',
                      backgroundColor: 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px'
                      }}>
                        <h4 style={{
                          margin: 0,
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#495057'
                        }}>
                          Paket {packageNum}
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <span style={{
                            fontSize: '12px',
                            color: '#6c757d',
                            backgroundColor: packageItems.length >= packageCapacity ? '#dc3545' : '#28a745',
                            padding: '2px 6px',
                            borderRadius: '12px',
                            marginBottom: '4px'
                          }}>
                            {packageItems.length}/{packageCapacity}
                          </span>
                          <span style={{ fontSize: '12px', color: '#6c757d' }}>
                            Tracking Nr: {packageTrackingNr}
                          </span>
                        </div>
                      </div>

                      {packageItems.map(item => (
                        <div key={item.id} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px',
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #e9ecef',
                          borderRadius: '4px',
                          marginBottom: '6px',
                          fontSize: '12px'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', color: '#495057' }}>
                              {item.itemCode}
                            </div>
                            <div style={{ color: '#6c757d', fontSize: '11px' }}>
                              Miktar: {item.quantity}
                            </div>
                          </div>
                          <Button
                            icon="minus"
                            stylingMode="text"
                            hint="Azalt"
                            onClick={() => {
                              // Paketten çıkar
                              removeFromPackage(item.id);
                              // DataGrid'deki miktarı güncelle
                              handleQuantityChange(item.id.split('-')[0], false);
                            }}
                            style={{
                              minWidth: '24px',
                              height: '24px',
                              color: '#dc3545'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  );
                })
              )}

              <div style={{
                marginTop: '20px',
                padding: '12px',
                backgroundColor: '#e9ecef',
                borderRadius: '6px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '5px' }}>
                  Aktif Paket
                </div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#495057' }}>
                  Paket {currentPackage}
                </div>
                <div style={{ fontSize: '11px', color: '#6c757d' }}>
                  {packages.filter(p => p.packageNumber === currentPackage).length}/{packageCapacity} dolu
                </div>
              </div>
            </div>
          </div>

          <div style={{
            position: 'absolute',
            bottom: '0',
            left: '0',
            right: '0',
            padding: '2%',
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #dee2e6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ fontSize: '14px', color: '#6c757d' }}>
              <span style={{ fontWeight: '600' }}>
                Toplam Paket: {getPackageNumbers().length} | Toplam Ürün: {packages.length}
              </span>
            </div>
            
            {packages.length > 0 && (
              <Button
                text="Paketle"
                type="success"
                stylingMode="contained"
                icon="check"
                onClick={() => {
                  notify('Tüm paketler başarıyla onaylandı!', 'success');
                  setPopupVisible(false);
                }}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  height: '48px'
                }}
              />
            )}
          </div>
        </Popup>
      </div>
    </div>
  );
};